import {
  extraErrorDataIntegration,
  httpClientIntegration,
} from "@sentry/react";
import * as Sentry from "@sentry/react";
import ReactDOM from "react-dom/client";
import "./index.css";

import "bootstrap/dist/css/bootstrap.min.css";

import "react-bootstrap-typeahead/css/Typeahead.css";

import {
  KINDE_ENVIRONMENT,
  KindeAuthenticatedUI,
  KindeRedirectProvider,
} from "@inrange/shared-components";

import { ReloginProvider } from "@inrange/building-manager-api-client";
import { GlobalStyle, ThemeProvider } from "@inrange/theme-components/styles";
import { QueryClientProvider } from "@tanstack/react-query";
import AppRoutes from "./AppRoutes";
import {
  liftOutreachGmailCallbackParams,
  redactOAuthCallbackParamsInEvent,
  redactOAuthCallbackParamsInRecordingEvent,
} from "./routes/leads/outreachGmailCallbackUrl";
import { UserProvider } from "./auth/UserContext";
import queryClient from "./queryClient";

// Before Sentry.init, which records the page URL, and before Kinde mounts - see the
// function for both reasons.
liftOutreachGmailCallbackParams();

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: "https://f9bb24586c704c40871064c57bf5c477@o1336164.ingest.sentry.io/4505561257738240",
    beforeSend(event) {
      // Filter out bogus errors from Outlook SafeLink / email link scanners.
      // These use a .NET WebBrowser control that emits COM interop errors like:
      // "Object Not Found Matching Id:X, MethodName:Y, ParamCount:Z"
      // See: https://github.com/getsentry/sentry-javascript/issues/3440
      const exception = event.exception?.values?.[0];
      const message = event.message || exception?.value || "";
      const exceptionType = exception?.type || "";

      if (/Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/.test(message)) {
        return null;
      }

      // Transient axios network errors, timeouts, and aborted requests are
      // user-side / connectivity issues, not application bugs (see
      // CUSTOMER-APP-C1/C4/C5 and the ADMIN-APP-8V-style network errors).
      if (
        exceptionType === "AxiosError" &&
        (message === "Network Error" ||
          message === "Request aborted" ||
          message === "canceled" ||
          /timeout/i.test(message) ||
          /aborted/i.test(message))
      ) {
        return null;
      }

      // Leaflet / GoogleMutant lifecycle races — a Leaflet map is torn down
      // while an async callback is still in flight, causing access to a
      // nulled `_map`, `_controlCorners`, or `_leaflet_pos`. Known upstream
      // bug (https://github.com/Leaflet/Leaflet/issues/9527). Not actionable
      // from our side (see ADMIN-APP-47, 4C, 8Q, 8G, 7Z, 8H, 87).
      //
      // Don't filter by stack-frame filename — at beforeSend time the SDK
      // sees the raw minified bundle URL (e.g. /assets/index-XXX.js), not
      // the post-symbolication Leaflet path. The message patterns below are
      // distinctive enough to stand alone.
      if (
        exceptionType === "TypeError" &&
        (/_leaflet_pos/.test(message) ||
          /_controlCorners/.test(message) ||
          /this\._map is null/.test(message) ||
          /controls_ready/.test(message) ||
          // GoogleMutant's `_initMutant` calls
          // `IntersectionObserver.observe(mapContainer)` from an async callback.
          // When the user navigates away mid-init the container is already
          // unmounted, so the target is no longer an Element and the observe
          // throws. Browser wordings differ ("must be an instance of Element" /
          // "parameter 1 is not of type 'Element'" / "does not implement
          // interface Element"), all caught by IntersectionObserver + Element
          // (see ADMIN-APP-9V).
          (/IntersectionObserver/.test(message) && /Element/.test(message)))
      ) {
        return null;
      }

      // GoogleMutant's `_setupGoogleMap` polls for `window.google` and throws
      // a plain Error (not TypeError) after 10 s if it never loads.
      if (/window\.google not found/.test(message)) {
        return null;
      }

      // Google Maps init races — the Maps JS SDK emits `Could not load
      // "<component>"` when a component fails to load from the Maps CDN
      // (network blip, ad-blocker, etc.). Not actionable from our side
      // (see ADMIN-APP-6X).
      if (/Could not load "(controls|places|marker|geometry|drawing)"/.test(message)) {
        return null;
      }

      return redactOAuthCallbackParamsInEvent(event);
    },
    // The Gmail OAuth code + state. The lift above takes them out of the address
    // bar, but the browser's navigation timing entry keeps the URL the page was
    // loaded with, and the pageload spans and replay's navigation entry are named
    // from it.
    beforeSendTransaction: (event) => redactOAuthCallbackParamsInEvent(event),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        beforeAddRecordingEvent: redactOAuthCallbackParamsInRecordingEvent,
      }),
      // 4xx is the application's responsibility - we already capture the
      // errors we care about via queryCache.onError in queryClient.ts (which
      // covers axios + react-query, the path nearly every XHR takes here).
      // Keeping 4xx in httpClientIntegration on top of that double-reports
      // every handled 4xx as an unhandled http.client error. Restrict to 5xx
      // so we keep a defence-in-depth net for genuine server errors from any
      // XHR source without doubling up on 4xx. The 503 carve-out remains
      // because battery API timeouts surface as 503.
      httpClientIntegration({
        failedRequestStatusCodes: [
          [500, 502],
          [504, 599],
        ],
      }),
      extraErrorDataIntegration(),
    ],
    tracesSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.VITE_BRANCH_NAME,
  });
}

const isDangerouslyUseLocalStorage =
  !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE;
if (!import.meta.env.PROD && import.meta.env.VITE_TEST_MODE) {
  document.body.classList.add("test-mode");
}

const root = ReactDOM.createRoot(document.getElementById("root")!);
root.render(
  <KindeRedirectProvider>
    <KindeAuthenticatedUI
      kindeClientId={KINDE_ENVIRONMENT.KINDE_ADMIN_APP_CLIENT_ID}
      useInsecureForRefreshToken={isDangerouslyUseLocalStorage}
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <GlobalStyle />
          <ReloginProvider>
            <UserProvider>
              <AppRoutes />
            </UserProvider>
          </ReloginProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </KindeAuthenticatedUI>
  </KindeRedirectProvider>
);
